import styled from '@emotion/styled';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const Page = styled.main({
  backgroundColor: '#181818',
  color: '#f5f5f5',
  minHeight: '100vh',
  padding: '64px 24px',
  '& a': {
    color: 'inherit',
    textDecoration: 'underline',
    textUnderlineOffset: '5px',
    overflowWrap: 'anywhere',
  },
  '& a:focus-visible': {
    outline: '2px solid #f5f5f5',
    outlineOffset: '6px',
  },
  '@media (max-width: 600px)': {
    padding: '32px 16px',
  },
});

const Content = styled.article({
  backgroundColor: '#282828',
  border: '1px solid #3b3b3b',
  borderRadius: '24px',
  margin: '0 auto',
  maxWidth: '1100px',
  padding: '32px',
  fontSize: '20px',
  lineHeight: 1.65,
  '& h1': { fontSize: '32px', lineHeight: 1.2, margin: '0 0 28px' },
  '& h2': { fontSize: '24px', lineHeight: 1.3, margin: '28px 0 24px' },
  '& p': { margin: '0 0 24px' },
  '& p:last-child': { marginBottom: 0 },
  '@media (max-width: 600px)': {
    padding: '24px',
    fontSize: '18px',
  },
});

const Contact = () => (
  <p>Contact: <a href="mailto:austin.w.chappell@gmail.com">austin.w.chappell@gmail.com</a></p>
);

const Layout: React.FC<{ title: string }> = ({ title, children }) => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;
    return () => { document.title = previousTitle; };
  }, [title]);

  return <Page><Content><h1>{title}</h1>{children}</Content></Page>;
};

const MailflowBackups: React.FC = () => (
  <Layout title="Mailflow Backups">
    <p>
      Mailflow Backups is a private backup tool operated by Austin Chappell for personal use.
      It saves encrypted backups of a self-hosted Mailflow installation to Austin’s Google
      Drive and supports restoring those backups.
    </p>
    <p>
      Google Drive access is used to upload, list, download, and delete backup files.
      Backups are encrypted before upload. This tool is not offered to the public and
      does not accept other users.
    </p>
    <p><Link to="/mailflow-backups/privacy">Privacy Policy</Link></p>
    <Contact />
  </Layout>
);

export const MailflowPrivacy: React.FC = () => (
  <Layout title="Mailflow Backups Privacy Policy">
    <p>Last updated: September 5, 2026</p>
    <p>
      Mailflow Backups is operated by Austin Chappell solely to back up and restore
      his personal self-hosted Mailflow installation.
    </p>
    <h2>Data access and use</h2>
    <p>
      The tool uses Google Drive authorization to upload, list, download, and delete
      backup files and access their associated metadata. Google authorization tokens
      are stored on the backup server to allow scheduled backups without repeated sign-ins.
    </p>
    <p>
      Backups may contain email messages, account settings, and application credentials.
      Backup contents are encrypted on the server before being uploaded to Google Drive.
    </p>
    <h2>Storage and sharing</h2>
    <p>
      Encrypted backups are stored in Austin’s personal Google Drive account. Temporary
      backup files may be created on the server during backup and restoration.
    </p>
    <p>
      Data is not sold, used for advertising, or shared with other users. Google provides
      the storage service. Google user data is not used to train AI models.
    </p>
    <h2>Retention and deletion</h2>
    <p>
      Backups remain in Google Drive until deleted manually or by a configured retention
      schedule. Deleted files may remain in Google Drive’s trash until it is emptied.
    </p>
    <p>
      Access can be revoked through the Google Account’s third-party connections settings.
      Revoking access stops future authorized operations but does not delete existing backups.
    </p>
    <h2>Google API data</h2>
    <p>
      Use of information received from Google APIs complies with the Google API Services
      User Data Policy, including its Limited Use requirements.
    </p>
    <h2>Contact</h2>
    <p>
      For questions or deletion requests, contact{' '}
      <a href="mailto:austin.w.chappell@gmail.com">austin.w.chappell@gmail.com</a>.
    </p>
  </Layout>
);

export default MailflowBackups;
